/**
 * Partner referral attribution across the Auth0 signup hop (POC-851).
 *
 * Anonymous `/referral?code=PF-…` must survive Universal Login. Auth0 owns
 * the authorize URL, so the raw `code` query cannot travel there. Persist
 * the partner code on first-party channels *before* `loginWithRedirect`:
 *
 *  - `localStorage.partnerReferralCode` (existing Callback readers)
 *  - first-party cookie `pf_partner_ref` (SameSite=Lax, short TTL)
 *  - Auth0 `appState.partnerReferralCode` (restored on /callback)
 *
 * One surviving channel is enough. `/callback` must never treat Auth0's
 * OAuth `code` as a partner code.
 */

import { firstPartyCookieDomain } from "./attribution";

export const PARTNER_REFERRAL_STORAGE_KEY = "partnerReferralCode";
export const PARTNER_REFERRAL_COOKIE = "pf_partner_ref";
export const AUTH0_APP_STATE_KEY = "auth0_app_state";

/** 24h — short enough to stay signup-scoped, long enough for email verify. */
export const PARTNER_REFERRAL_TTL_SECONDS = 24 * 60 * 60;

/** Partner codes are `PF-` + slug (see partners model). Reject OAuth junk. */
const CODE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{1,63}$/;

export type PartnerReferralAppState = {
	partnerReferralCode?: string;
	[key: string]: unknown;
};

/**
 * Apex cookie on the product domain only — same rule as `pf_attr`
 * (`firstPartyCookieDomain`). Preview hosts (e.g. *.vercel.app) stay
 * host-only; a last-two-labels Domain= would hit the Public Suffix List
 * and drop the cookie.
 */
export const partnerReferralCookieDomain = (hostname: string): string =>
	firstPartyCookieDomain(hostname);

export const sanitizePartnerReferralCode = (
	value: string | null | undefined,
): string | null => {
	if (typeof value !== "string") return null;
	const trimmed = value.trim();
	if (!CODE_PATTERN.test(trimmed)) return null;
	return trimmed;
};

const readCookie = (name: string): string | null => {
	try {
		const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
		if (!match) return null;
		return decodeURIComponent(match[1]);
	} catch {
		return null;
	}
};

const writeCookie = (name: string, value: string, maxAge: number): void => {
	try {
		const hostname = window.location.hostname;
		// biome-ignore lint/suspicious/noDocumentCookie: sync first-party write before Auth0
		document.cookie = `${name}=${encodeURIComponent(
			value,
		)}; path=/${partnerReferralCookieDomain(hostname)}; max-age=${maxAge}; SameSite=Lax`;
	} catch {
		// Cookies blocked — other channels may still survive.
	}
};

const expireCookie = (name: string): void => {
	try {
		const hostname = window.location.hostname;
		// biome-ignore lint/suspicious/noDocumentCookie: must clear both host and apex
		document.cookie = `${name}=; path=/; max-age=0`;
		if (partnerReferralCookieDomain(hostname)) {
			// biome-ignore lint/suspicious/noDocumentCookie: apex-scoped twin
			document.cookie = `${name}=; path=/${partnerReferralCookieDomain(
				hostname,
			)}; max-age=0`;
		}
	} catch {
		// ignore
	}
};

const readStoredAppState = (): PartnerReferralAppState | null => {
	try {
		const raw = localStorage.getItem(AUTH0_APP_STATE_KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw);
		return parsed && typeof parsed === "object"
			? (parsed as PartnerReferralAppState)
			: null;
	} catch {
		return null;
	}
};

/**
 * The in-app path the user was on when a re-auth redirect started (#1869:
 * the Security MFA action sets `appState.returnTo`).
 *
 * Only same-origin, in-app paths are honoured — `//evil.com` and absolute URLs
 * are rejected so a tampered appState can never turn the Auth0 callback into an
 * open redirect — and `/callback` itself is skipped to avoid a loop.
 */
export const readReturnToPath = (): string | null => {
	const returnTo = readStoredAppState()?.returnTo;
	if (typeof returnTo !== "string") return null;
	const path = returnTo.trim();
	if (!path.startsWith("/") || path.startsWith("//")) return null;
	if (path === "/callback" || path.startsWith("/callback?")) return null;
	return path;
};

/**
 * The sale `/verify-buyer` put in Auth0 `appState`, for browsers whose
 * localStorage did not survive the sign-in redirect (Apple Mail,
 * POCKETSFLOW-FRONTEND-9K). Only a Mongo ObjectId is accepted.
 */
export const readCheckoutSaleFromAppState = (): string | null => {
	const saleId = readStoredAppState()?.checkoutSaleId;
	return typeof saleId === "string" && /^[a-f0-9]{24}$/i.test(saleId)
		? saleId
		: null;
};

/**
 * Write every durable channel. Safe to call repeatedly; last valid code wins.
 * Returns the sanitized code, or null if it was rejected.
 */
export const persistPartnerReferralCode = (
	code: string | null | undefined,
): string | null => {
	const sanitized = sanitizePartnerReferralCode(code);
	if (!sanitized) return null;

	try {
		localStorage.setItem(PARTNER_REFERRAL_STORAGE_KEY, sanitized);
	} catch {
		// Private mode / blocked storage — cookie + appState may still work.
	}
	writeCookie(PARTNER_REFERRAL_COOKIE, sanitized, PARTNER_REFERRAL_TTL_SECONDS);
	return sanitized;
};

/**
 * Read the first surviving channel. Does not re-persist.
 */
export const readPartnerReferralCode = (
	appState?: PartnerReferralAppState | null,
): string | null => {
	const fromArg = sanitizePartnerReferralCode(appState?.partnerReferralCode);
	if (fromArg) return fromArg;

	try {
		const fromStorage = sanitizePartnerReferralCode(
			localStorage.getItem(PARTNER_REFERRAL_STORAGE_KEY),
		);
		if (fromStorage) return fromStorage;
	} catch {
		// ignore
	}

	const fromCookie = sanitizePartnerReferralCode(
		readCookie(PARTNER_REFERRAL_COOKIE),
	);
	if (fromCookie) return fromCookie;

	return sanitizePartnerReferralCode(readStoredAppState()?.partnerReferralCode);
};

/**
 * Restore a missing localStorage value from appState and/or cookie so the
 * existing Callback `createUser.referral` path keeps working.
 */
export const restorePartnerReferralCode = (
	appState?: PartnerReferralAppState | null,
): string | null => {
	const code = readPartnerReferralCode(appState);
	if (!code) return null;
	persistPartnerReferralCode(code);
	return code;
};

export const clearPartnerReferralCode = (): void => {
	try {
		localStorage.removeItem(PARTNER_REFERRAL_STORAGE_KEY);
	} catch {
		// ignore
	}
	expireCookie(PARTNER_REFERRAL_COOKIE);
	// Strip the partner field from stored Auth0 appState so a later
	// restore/read cannot resurrect attribution after a successful signup.
	try {
		const raw = localStorage.getItem(AUTH0_APP_STATE_KEY);
		if (!raw) return;
		const parsed = JSON.parse(raw) as PartnerReferralAppState;
		if (!parsed || typeof parsed !== "object") return;
		if (!("partnerReferralCode" in parsed)) return;
		delete parsed.partnerReferralCode;
		localStorage.setItem(AUTH0_APP_STATE_KEY, JSON.stringify(parsed));
	} catch {
		// ignore
	}
};

/**
 * Persist from `/referral?code=` only. Never run this on `/callback` — Auth0
 * owns that `code` query param.
 *
 * A non-empty `code` that fails sanitization clears prior attribution so a
 * stale localStorage / cookie / appState value cannot ride along on signup.
 */
export const capturePartnerReferralFromLocation = (
	location: Pick<Location, "pathname" | "search"> = window.location,
): string | null => {
	if (location.pathname !== "/referral") return null;
	const code = new URLSearchParams(location.search).get("code");
	if (code == null || code === "") return null;
	const persisted = persistPartnerReferralCode(code);
	if (!persisted) {
		clearPartnerReferralCode();
		return null;
	}
	return persisted;
};

/** Auth0 must not treat `/referral?code=PF-…` as an OAuth callback. */
export const shouldSkipAuth0RedirectCallback = (
	pathname: string = window.location.pathname,
): boolean => pathname === "/referral";
